import { Component } from "react";
import { connect, ConnectedComponent } from "react-redux";
import { TokenState, IStore } from "../../DataTypes/storeTypes";
import { onSetExistingClientCredentialsSubmit } from "../../Actions/actions";
import { Field, reduxForm, formValueSelector, clearFields } from "redux-form";
import { required, minLength6, fixedLength6, maxLength30, email } from "../../../Common/Form/formValidationRules";
import TextInput from "../../../Common/Form/TextInput";
import { Button } from "@powerdiary/components-library/lib";

interface IProps {
    isBusy: boolean;
    isSmsType: boolean;
    state: TokenState;
    errorMessage: string;

    onSubmit(values);
}

interface IFormProps extends IProps {
    passwordValue?: string;

    handleSubmit?: any;
}

class SetExistingClientCredentials extends Component<IFormProps> {
    validatePassword = (value: string) => {
        return value && value !== this.props.passwordValue
            ? "The password and confirmation password do not match."
            : undefined;
    };

    render() {
        return (
            <>
                <div className="row">
                    <div className="col-12">
                        <form onSubmit={this.props.handleSubmit}>
                            <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">Email</label>
                                <div className="col-12 col-md-8">
                                    <Field
                                        name="email"
                                        type="email"
                                        component={TextInput}
                                        validate={[required, email]}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">New Password</label>
                                <div className="col-12 col-md-8">
                                    <Field
                                        name="password"
                                        type="password"
                                        component={TextInput}
                                        validate={[required, minLength6, maxLength30]}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">Confirm New Password</label>
                                <div className="col-12 col-md-8">
                                    <Field
                                        name="confirmPassword"
                                        type="password"
                                        component={TextInput}
                                        validate={[required, required, this.validatePassword]}
                                    />
                                </div>
                            </div>

                            {this.props.state === TokenState.ShowToken && (
                                <div className="form-group row">
                                    <label className="col-12 col-md-4 col-form-label">Verification code</label>
                                    <div className="col-12 col-md-8">
                                        <Field
                                            name="token"
                                            type="text"
                                            component={TextInput}
                                            validate={[required, fixedLength6]}
                                        />
                                        <small className="form-text text-muted">
                                            Please enter the verification code received.
                                        </small>
                                    </div>
                                </div>
                            )}

                            {this.props.errorMessage && (
                                <div className="alert alert-danger" role="alert">
                                    <div>{this.props.errorMessage}</div>
                                </div>
                            )}

                            <div className="row mt-4">
                                <div className="col text-center">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        size="large"
                                        className="btn-block"
                                        disabled={this.props.isBusy}
                                    >
                                        {this.props.state === TokenState.Initial ? "Get verification code" : "Submit"}
                                    </Button>
                                    {this.props.state === TokenState.Initial && (
                                        <small className="text-muted">
                                            {`A verification code will be sent to your ${this.props.isSmsType ? "mobile number" : "email"}.`}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </>
        );
    }
}

const FormConnected = reduxForm({
    form: "setExistingClientCredentialsForm",
    getFormState: (state: IStore) => state.forms.setExistingClientCredentialsForm,
})(SetExistingClientCredentials);

const selector = formValueSelector(
    "setExistingClientCredentialsForm",
    (state: IStore) => state.forms.setExistingClientCredentialsForm
);

const mapStateToProps = (state: IStore, ownProps: IFormProps): IFormProps => {
    const passwordValue = selector(state, "password");

    const isSmsType = state.initialSettings.organisationInfo.tokenVia === "sms";

    return {
        ...ownProps,
        passwordValue: passwordValue,
        state: state.common.setExistingClientCredentialsPageState,
        isBusy: state.common.isBusy,
        isSmsType: isSmsType,
        errorMessage: state.common.errorMessage,
    };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IProps => {
    return {
        ...ownProps,
        onSubmit: (values) => {
            dispatch(
                onSetExistingClientCredentialsSubmit(values, () => {
                    dispatch(clearFields("setExistingClientCredentialsForm", false, true, "token"));
                })
            );
        },
    };
};

export const SetExistingClientCredentialsConnected: ConnectedComponent<typeof FormConnected, any> = connect(
    mapStateToProps,
    mapDispatchToProps
)(FormConnected);
